export type PontoonTier = {
  min_nights: number;
  max_nights: number;
  rate_per_day: number;
};

export const DEFAULT_PONTOON_TIERS: PontoonTier[] = [
  { min_nights: 1, max_nights: 3, rate_per_day: 500 },
  { min_nights: 4, max_nights: 6, rate_per_day: 400 },
  { min_nights: 7, max_nights: 999, rate_per_day: 250 },
];

export function calculatePontoonCost(
  nights: number,
  dailyRate: number,
  tiers: PontoonTier[]
): { dailyCost: number; packageCost: number; packageRatePerDay: number; usePackage: boolean } {
  const dailyCost = nights * dailyRate;

  const sortedTiers = [...tiers].sort((a, b) => a.min_nights - b.min_nights);
  let matchedTier: PontoonTier | null = null;
  for (const tier of sortedTiers) {
    if (nights >= tier.min_nights && nights <= tier.max_nights) {
      matchedTier = tier;
      break;
    }
  }

  const packageRatePerDay = matchedTier ? matchedTier.rate_per_day : dailyRate;
  const packageCost = nights * packageRatePerDay;
  const usePackage = packageCost < dailyCost;

  return { dailyCost, packageCost, packageRatePerDay, usePackage };
}

// ── Duration price ladder ───────────────────────────────────────────────────
// Lake Darling–style explicit per-night-count total-price map. Instead of a
// flat $/day-within-a-tier rate, the host sets the *total* price for each stay
// length, letting the curve discount per added night. This is the single source
// of truth for pontoon pricing across server, web, and mobile.

export type PontoonLadderEntry = {
  /** Number of nights of the stay (1-based). */
  nights: number;
  /** Total rental price (USD, whole dollars) for a stay of this many nights. */
  total: number;
};

/**
 * Recommended starting ladder (host-editable). Anchors a single night near
 * Lake Darling Tier B and lets the multi-day rate ratchet toward Tier A, with
 * the 7-night landing at $2,400 (+37% over the proven $1,750).
 */
export const DEFAULT_PONTOON_LADDER: PontoonLadderEntry[] = [
  { nights: 1, total: 850 },
  { nights: 2, total: 1250 },
  { nights: 3, total: 1600 },
  { nights: 4, total: 1850 },
  { nights: 5, total: 2050 },
  { nights: 6, total: 2250 },
  { nights: 7, total: 2400 },
];

/**
 * Validate, coerce, de-duplicate (last value wins per night-count) and sort a
 * raw ladder value into a clean ascending list. Accepts unknown input so it can
 * be fed straight from JSON.parse of a stored setting.
 */
export function normalizePontoonLadder(input: unknown): PontoonLadderEntry[] {
  if (!Array.isArray(input)) return [];
  const byNights = new Map<number, number>();
  for (const raw of input) {
    if (!raw || typeof raw !== "object") continue;
    const n = Math.round(Number((raw as any).nights));
    const t = Math.round(Number((raw as any).total));
    if (!Number.isFinite(n) || !Number.isFinite(t)) continue;
    if (n < 1 || t < 0) continue;
    byNights.set(n, t);
  }
  return [...byNights.entries()]
    .map(([nights, total]) => ({ nights, total }))
    .sort((a, b) => a.nights - b.nights);
}

/** Highest night-count explicitly present in the ladder (0 if empty). */
export function pontoonLadderMaxNights(ladder: PontoonLadderEntry[]): number {
  const norm = normalizePontoonLadder(ladder);
  return norm.length > 0 ? norm[norm.length - 1].nights : 0;
}

/**
 * Compute the total rental price for a given number of nights from the ladder.
 *
 * - Exact match → that entry's total.
 * - Beyond the table → extend with the last marginal step (the per-night
 *   difference between the final two entries; falls back to the last entry's
 *   effective per-night rate when only one entry exists).
 * - Below the smallest entry → prorate the first entry's effective per-night
 *   rate (defensive; the default table starts at 1 night so this is rare).
 * - Gaps inside the table → linear interpolation between neighbours.
 *
 * `perDay` is the effective $/night (rounded) used purely for display framing.
 * `inTable` is true when the night-count is an exact ladder entry.
 */
export function calculatePontoonLadderCost(
  nights: number,
  ladder: PontoonLadderEntry[]
): { total: number; perDay: number; inTable: boolean } {
  if (!Number.isFinite(nights) || nights <= 0) return { total: 0, perDay: 0, inTable: false };
  const norm = normalizePontoonLadder(ladder);
  if (norm.length === 0) return { total: 0, perDay: 0, inTable: false };

  const exact = norm.find((e) => e.nights === nights);
  if (exact) {
    return { total: exact.total, perDay: Math.round(exact.total / nights), inTable: true };
  }

  const first = norm[0];
  const last = norm[norm.length - 1];

  if (nights < first.nights) {
    const total = Math.round((first.total / first.nights) * nights);
    return { total, perDay: Math.round(total / nights), inTable: false };
  }

  if (nights > last.nights) {
    const prev = norm.length >= 2 ? norm[norm.length - 2] : null;
    const step = prev
      ? Math.max(0, (last.total - prev.total) / (last.nights - prev.nights))
      : last.total / last.nights;
    const total = Math.round(last.total + step * (nights - last.nights));
    return { total, perDay: Math.round(total / nights), inTable: false };
  }

  // Gap inside the table: interpolate between the surrounding entries.
  let lower = first;
  let upper = last;
  for (const e of norm) {
    if (e.nights <= nights) lower = e;
    if (e.nights >= nights) {
      upper = e;
      break;
    }
  }
  if (upper.nights === lower.nights) {
    return { total: lower.total, perDay: Math.round(lower.total / nights), inTable: false };
  }
  const frac = (nights - lower.nights) / (upper.nights - lower.nights);
  const total = Math.round(lower.total + frac * (upper.total - lower.total));
  return { total, perDay: Math.round(total / nights), inTable: false };
}

/**
 * The premium value props that justify anchoring above the proven rate. Single
 * source of truth so web + mobile + AI chat present the same convenience moat.
 */
export const PONTOON_VALUE_PROPS: { title: string; desc: string }[] = [
  { title: "At your own dock", desc: "Docked at the cabin — no trailering, no ramp lines" },
  { title: "Yours 24/7, no nightly return", desc: "The boat stays at the dock for your whole stay" },
  { title: "First tank of gas free", desc: "We fuel it up before you arrive" },
];
